import { ControlType, Property, PropertyType } from "../../../base/types";
import { FC, useEffect, useState } from "react";
import { IControlAttributes, IPageLink } from "../../../base/models";
import { Registry } from "../../../base/register";
import { useGetProperties } from "../../../base/hooks";
import { useAuth } from "../../../../modules/auth";
import { ErrorMessage, Spinner, useCalcRemainingTime } from "../../../widgets";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageLinkHelper } from "../../../base/helpers";
import { IExtended, useGetCustomConnectionsQuery, useGetExtendedGatePassQuery, useResendApprovalMutation, useUpdateExtendedLineMutation, useUpdateNoApprovalMutation } from "./properties/extendedApi";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { KTSVG } from "../../../../helpers";
import { addMinutes } from "../timeline/helper";
import { useInterval } from "react-use";
import { ServiceRequestApproval } from "./ServiceRequestApproval";
import { ServiceRequestActions } from "./ServiceRequestActions";
import { useGetLookupNames } from "./properties/useGetLookupNames";



const useGetNameFromGuid = (guid: string) => {
    const { getLookupLabel } = useGetLookupNames();
    const [name, setName] = useState<string | undefined>();

    useEffect(() => {
        if (guid) {
            getLookupLabel({ value: guid })
                .then(fetchedName => setName(fetchedName))
                .catch(err => console.error('Error fetching name:', err));
        }
    }, [guid, getLookupLabel]);

    return name;
};

export const ExtendGatePassComponent: FC<IControlAttributes> = ({
    control,
    className,
    children,
    props,
    designerMode,
    ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });
    const navigateTo: IPageLink | undefined = properties[Property.CancelPageLink];
    const navigateToOnCancel: IPageLink | undefined = properties[Property.ReOpenPageLink];
    const enableApproval: boolean | undefined = properties[Property.EnableApproval];
    const enableSubmit: boolean | undefined = properties[Property.EnableSubmit];
    const enableRefetch: boolean | undefined = properties[Property.EnableRefetch];
    const enableResend: boolean | undefined = properties[Property.EnableResend];
    const { user, siteId } = useAuth();

    const queryParams = new URLSearchParams(window.location.search);
    const idFromUrl = queryParams.get('id');
    const navigate = useNavigate();
    const [updateRecord] = useUpdateExtendedLineMutation();
    const [updateNoApprovalRecord] = useUpdateNoApprovalMutation();
    const [resendRequest] = useResendApprovalMutation();
    const { data: GPExtention, refetch } = useGetExtendedGatePassQuery({ id: idFromUrl || '' });
    const { data: CustomConnections } = useGetCustomConnectionsQuery({ contact: user?.contactId || '' });
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const endTime = addMinutes(GPExtention?.sendTime, GPExtention?.otpTimer);
    const { isFuture } = useCalcRemainingTime({ endTime });
    const [searchParams] = useSearchParams();
    const approvalBy = useGetNameFromGuid(GPExtention?.approvalBy || '')
    const [formData, setFormData] = useState({
        toDate: GPExtention?.toDate ? new Date(GPExtention.toDate) : new Date(),
        fromDate: GPExtention?.fromDate ? new Date(GPExtention.fromDate) : new Date(),
        approvalBy: GPExtention?.approvalBy || "",
    });
    const [errors, setErrors] = useState({
        fromDate: "",
        approvalBy: "",
    });

    const [loading, setLoading] = useState(false);
    const [loading1, setLoading1] = useState(false);

    useInterval(() => {
        if (isFuture !== true) {
            refetch()
        }
    }, (!endTime || !isFuture) ? 5000 : null)

    useInterval(() => {
        if (enableRefetch && isFuture) {
            refetch();
        }
    }, enableRefetch && isFuture ? 5000 : null);

    useEffect(() => {
        if (GPExtention?.approvalBy && formData.approvalBy === "") {
            setFormData(prev => ({ ...prev, approvalBy: GPExtention?.approvalBy || '' }));
        }

        // eslint-disable-next-line 
    }, [GPExtention?.approvalBy]);

    useEffect(() => {
        if (GPExtention?.toDate) {
            setSelectedDate(new Date(GPExtention.toDate));
        }
    }, [GPExtention]);

    useEffect(() => {

        if (siteId && navigateTo && navigateTo.pageId && (GPExtention?.status === 365970000 || GPExtention?.status === 365970001)) {
            navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateTo));
        }

        // eslint-disable-next-line
    }, [GPExtention?.status])

    const validateForm = (approvalRequired?: boolean) => {
        const newErrors: any = {};

        if (!formData.fromDate) newErrors.fromDate = "From Date & Time is required";
        if (!formData.approvalBy && approvalRequired) newErrors.approvalBy = "Approval By is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleUpdateRow = async () => {
        if (!validateForm(GPExtention?.approvalRequired)) return;
        setLoading(true);
        setErrorMessage("");

        try {
            if (GPExtention?.approvalRequired) {
                const data = await updateRecord({
                    status: 365970002,
                    toDate: selectedDate ?? undefined,
                    id: GPExtention?.id,
                    approvalBy: formData.approvalBy,
                }).unwrap();

                await resendRequest(data.id || '')
                    .unwrap()
                    .catch((err) => {
                        const message = err?.data?.message || "Error processing request";
                        setErrorMessage(message);
                    })

                refetch();
            } else {
                await updateNoApprovalRecord({
                    id: GPExtention?.id,
                    status: 365970000,
                    toDate: selectedDate ?? undefined,
                }).unwrap();

                if (siteId && navigateTo?.pageId) {
                    navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateTo));
                }
            }
        } catch (err: any) {
            const message = err?.data?.message || "Error processing request";
            setErrorMessage(message);
        } finally {
            setLoading(false);
        }
    };

    const hasValidAuthToken = () => {
        const approvalToken: string | undefined = searchParams.get("reqId") || undefined
        return GPExtention?.approvalToken && GPExtention?.approvalToken === approvalToken
    }

    const handleClick = async (isApprovalRequired: boolean) => {
        setLoading1(true);
        setErrorMessage("");

        const request: IExtended = {
            id: GPExtention?.id,
            multidaygprequired: isApprovalRequired,
            approvalRequired: isApprovalRequired
        };

        try {
            await updateNoApprovalRecord(request).unwrap();
            await refetch();
        } catch (err: any) {
            setErrorMessage(err?.data?.message || 'An error occurred while processing your request.');
        } finally {
            setLoading1(false);
        }
    };

    const onCancel = () => {
        if (siteId && navigateToOnCancel && navigateToOnCancel.pageId)
            navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToOnCancel))
    }

    return (
        <div className="container mt-4 p-3 bg-white rounded">
            <div className="row">
                <div className="col-md-6">
                    <div className="mb-3">
                        <label htmlFor="fromDate" className="form-label">From Date & Time</label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                value={dayjs(GPExtention?.fromDate)}
                                // onChange={handleDateChange}
                                format="DD-MM-YYYY HH:mm"
                                timeSteps={{ minutes: 1 }}
                                disabled={true}
                                slotProps={{
                                    textField: {
                                        className: `form-control form-control-sm ${className || ""}`.trim(),
                                        sx: {
                                            "& .MuiOutlinedInput-input": { padding: "5px" },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="mb-3">
                        <label htmlFor="toDate" className="form-label">To Date & Time</label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                value={selectedDate ? dayjs(selectedDate) : null}
                                onChange={(date) => setSelectedDate(date ? date.toDate() : null)}
                                format="DD-MM-YYYY HH:mm"
                                timeSteps={{ minutes: 1 }}
                                disabled={GPExtention?.status !== 1}
                                slotProps={{
                                    textField: {
                                        className: `form-control form-control-sm ${className || ""}`.trim(),
                                        sx: {
                                            "& .MuiOutlinedInput-input": { padding: "5px" },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    {errors.fromDate && <div className="text-danger">{errors.fromDate}</div>}
                </div>

                {GPExtention?.approvalRequired && (
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="approvalBy" className="form-label">Approval By</label>
                            <select
                                className="form-control form-control-sm"
                                id="approvalBy"
                                name="approvalBy"
                                value={formData.approvalBy}
                                disabled={GPExtention.status !== 1}
                                onChange={(e) => setFormData(prev => ({ ...prev, approvalBy: e.target.value }))}
                                style={{ height: "28px", padding: "5px" }}
                            >
                                {formData.approvalBy && !CustomConnections?.some(conn => conn.customconnectionid === formData.approvalBy) && (
                                    <option value={formData.approvalBy}>{approvalBy}</option>
                                )}

                                <option value=""></option>

                                {CustomConnections?.map((connection) => (
                                    <option
                                        key={connection.customconnectionid}
                                        value={connection.customconnectionid}
                                    >
                                        {connection.name}
                                    </option>
                                ))}
                            </select>

                        </div>
                        {errors.approvalBy && <div className="text-danger">{errors.approvalBy}</div>}
                    </div>

                )}
            </div>

            {(enableSubmit && GPExtention?.status === 1) && <div className="d-flex justify-content-end mt-4">
                <button type='button'
                    onClick={handleUpdateRow}
                    disabled={loading}
                    className='btn btn-lg btn-primary me-3'>
                    <span className='indicator-label'>
                        {loading && <Spinner className='me-2' show={loading} />}
                        <span>Request For Gate Pass</span>

                        <KTSVG
                            path='/media/icons/duotune/arrows/arr064.svg'
                            className='svg-icon-3 ms-2 me-0'
                        />
                    </span>
                </button>
            </div>}
            {(enableApproval && hasValidAuthToken()) && <ServiceRequestApproval GPExtention={GPExtention} refetch={refetch} />}
            {(enableResend) && <ServiceRequestActions GPExtention={GPExtention} onCancel={onCancel} setErrorMessage={setErrorMessage} />}

            {errorMessage === "Proceed with multiple day gate pass extension!" ? (
                <div className="mb-2 text-danger">
                    {/* Display message followed by the link */}
                    Proceed with multiple day gate pass extension!{" "}
                    <button
                        className="btn btn-link text-primary fs-6 fw-bold mx-1"
                        type="button"
                        onClick={() => handleClick(true)}>
                        {loading1 && <Spinner show={loading1} />}
                        Click here
                    </button>
                </div>
            ) :
                GPExtention?.status === 1 &&
                    GPExtention.multidaygprequired ? (<>
                        <div className="mb-2 text-danger">
                            {/* Display message followed by the link */}
                            Proceed with single day gate pass extension!{" "}
                            <button
                                type="button"
                                className="btn btn-link text-primary fs-6 fw-bold mx-1"
                                onClick={() => handleClick(false)}>
                                {loading1 && <Spinner show={loading1} />}
                                Click here
                            </button>
                        </div>
                    </>) :
                    (
                        <></>
                    )}
            {
                (errorMessage && errorMessage !== "Proceed with multiple day gate pass extension!") && <ErrorMessage className="my-3 fs-8" show={!!errorMessage} message={errorMessage} />
            }
        </div>
    );
};

Registry.addControl({
    id: "dad7cbba-4259-45f3-8f2a-c950a9758f25",
    name: "Extend Gate Pass Component",
    type: ControlType.ExtendGatePassComponent,
    properties: [
        { id: Property.Tooltip, name: "Tooltip", type: PropertyType.String },
        { id: Property.Label, name: "Navigate Label", type: PropertyType.String },
        { id: Property.CancelPageLink, name: 'Navigate To', type: PropertyType.PageLink },
        { id: Property.ReOpenPageLink, name: 'Navigate To Cancel', type: PropertyType.PageLink },
        { id: Property.EnableApproval, name: 'Enable Approval', type: PropertyType.Boolean },
        { id: Property.EnableRefetch, name: 'Enable Refetch', type: PropertyType.Boolean },
        { id: Property.EnableSubmit, name: 'Enable Submit', type: PropertyType.Boolean },
        { id: Property.EnableResend, name: 'Enable Resend', type: PropertyType.Boolean },
    ],
    component: ExtendGatePassComponent,
});
